import { RootState } from "@/store/store";
import {
  convertThreeToIElementTransform,
  parseQuaternion,
  parseVector3,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import {
  IElement,
  IElementGenericImgSheet,
  IElementImg360,
  IElementSection,
} from "@faro-lotv/ielement-types";
import {
  Mutation,
  MutationSetElementPose,
  MutationSetImg360Rotation,
  createMutationSetAreaWorldPose,
  createMutationSetElementPose,
  createMutationSetImg360Rotation,
  createMutationSetLayerToAreaPose,
} from "@faro-lotv/service-wires";
import { Matrix4, Vector3, Vector3Tuple, Vector4Tuple } from "three";
import {
  AlignmentTransform,
  calculateAlignmentTransform,
} from "./alignment-transform";

/**
 * Create a mutation to update the pose of a PointCloud IElement
 *
 * @param pcSection Section of the PointCloud to move
 * @param transform the new pose of the PointCloud
 * @returns The mutation to apply to the Project
 */
export function createSetPointCloudPoseMutation(
  pcSection: IElementSection,
  transform?: AlignmentTransform,
): MutationSetElementPose {
  return createMutationSetElementPose(
    pcSection.id,
    transform
      ? {
          ...convertThreeToIElementTransform({
            position: parseVector3(transform.position),
            quaternion: parseQuaternion(transform.quaternion),
            scale: new Vector3(1, 1, 1),
          }),
          isWorldScale: true,
        }
      : null,
  );
}

/**
 * Create a mutation to align an element.
 *
 * @param alignedElement The IElement that was being aligned by the user.
 * @param newAlignedElementTransform The new transform of the element aligned by the user, in world space.
 * @param elementToApplyTransform The element where the new transform should be persisted. Must be an ancestor of `alignedElement`.
 * @param state Root state of the application.
 * @returns The mutation to persist the new transform to the Project API.
 */
export function createAlignmentMutation(
  alignedElement: IElement,
  newAlignedElementTransform: Matrix4,
  elementToApplyTransform: IElementSection,
  state: RootState,
): MutationSetElementPose {
  const newLocalTransformOfElementToApply = calculateAlignmentTransform(
    alignedElement,
    newAlignedElementTransform,
    elementToApplyTransform,
    state,
  );

  // Create a mutation for the Project API
  return createSetPointCloudPoseMutation(
    elementToApplyTransform,
    newLocalTransformOfElementToApply,
  );
}

/**
 * @param v scale vector
 * @param eps The margin of error to allow
 * @returns whether the vector is uniform within a small margin of error
 */
function isScaleUniform(v: Vector3 | Vector3Tuple, eps = 0.0001): boolean {
  v = parseVector3(v);
  return Math.abs(v.x - v.y) < eps && Math.abs(v.x - v.z) < eps;
}

/**
 * Creates the mutation payload for a alignment of the area to CAD,
 * all CADs are geo-referenced and have same transform
 *
 * @param area The area being aligned to the CAD.
 * @param newTransform The area-to-cad transform received at the end of manual alignment from alignment page
 * @returns The mutations payload to send to the ProjectAPI
 */
export function createAreaAlignmentMutations(
  area: IElementSection,
  newTransform: AlignmentTransform,
): Mutation[] {
  assert(isScaleUniform(newTransform.scale), "Expected scale to be uniform");

  const areaMutations: Mutation[] = [
    createMutationSetAreaWorldPose(
      area.id,
      newTransform.scale[0],
      {
        x: newTransform.quaternion[0],
        y: newTransform.quaternion[1],
        z: newTransform.quaternion[2],
        w: newTransform.quaternion[3],
      },
      {
        x: newTransform.position[0],
        y: newTransform.position[1],
        z: newTransform.position[2],
      },
    ),
  ];

  return areaMutations;
}

/**
 * Creates the mutation payload for a sheet-to-area alignment,
 *
 * @param sheet The sheet being aligned
 * @param newTransform The sheet-to-area transform computed from 2 points alignment component
 * @returns The mutations payload to send to the ProjectAPI
 */
export function createSheetAlignmentMutations(
  sheet: IElementGenericImgSheet,
  newTransform: AlignmentTransform,
): Mutation[] {
  assert(isScaleUniform(newTransform.scale), "Expected scale to be uniform");

  const sheetMutations: Mutation[] = [
    createMutationSetLayerToAreaPose(
      sheet.id,
      newTransform.scale[0],
      {
        x: newTransform.quaternion[0],
        y: newTransform.quaternion[1],
        z: newTransform.quaternion[2],
        w: newTransform.quaternion[3],
      },
      {
        x: newTransform.position[0],
        y: newTransform.position[1],
        z: newTransform.position[2],
      },
    ),
  ];

  return sheetMutations;
}

/**
 * Create a mutation to update the accurate relative rotation component of a 360 image IElement
 *
 * @param imageElement IElement of the 360 image to update the POSE
 * @param quaternion the new rotation component of the 360 image with respect to its parent
 * @returns The mutation to apply to the Project
 */
export function createSet360ImageRotationMutation(
  imageElement: IElementImg360,
  quaternion?: Vector4Tuple,
): MutationSetImg360Rotation {
  // assume this rotation is always accurate
  const isRotationAccurate = true;
  return createMutationSetImg360Rotation(
    imageElement.id,
    quaternion
      ? {
          x: quaternion[0],
          y: quaternion[1],
          z: quaternion[2],
          w: quaternion[3],
        }
      : null,
    isRotationAccurate,
  );
}
